import React, { useEffect, useState } from "react";
import Modal from "../../Modal";
import instance from "../../../api/instance";
// import { toast } from "react-toastify";
import { useRedux } from "../../../states/redux-state";
import {
    ThemeProvider,
    Theme,
    StyledEngineProvider,
    Switch,
    createTheme,
    adaptV4Theme,
} from "@mui/material";
import { nanoid } from "nanoid";
import { localStorage } from "../../../shared/storage";
import WarningBlock from '../../core/WarningBlock';
import styles from './PublicLinkModal.module.scss'
import { ReactComponent as CopyIcon } from '../../../assets/svg/content_copy.svg';
import { iKmlFile, watchKmlFiles } from '../../../shared/db/kml-db';
import {
    getShareLinkConfig,
    setShareLinkKml,
    setShareLinkGeofence,
    getAllFencesFlat,
    iShareLinkConfig,
    iShareLinkFence,
} from '../../../shared/db/share-link-config-db';
import { iFenceDetails, FenceType } from '../../../shared/interfaces';



const PublicLinkModal = ({ setIsPublicLinkModalOpen, tagId }) => {

    const [isPublicLinkOn, setIsPublicLinkOn] = useState(false);
    const [publicLinkTokenFromDb, setPublicLinkTokenFromDb] = useState<string | null>(null);

    // KML / geofence state
    const [kmlFiles, setKmlFiles] = useState<{ [id: string]: iKmlFile }>({});
    const [fences, setFences] = useState<{ [id: string]: iFenceDetails }>({});
    const [shareLinkConfig, setShareLinkConfig] = useState<iShareLinkConfig>({ kml: {}, geofences: {} });
    const [layersLoading, setLayersLoading] = useState(false);

    const checkIfPublicLinkEnabled = async () => {
        const URL = `/api/tag/one?firebaseId=${tagId}`;

        await instance.get(URL)
            .then(response => {
                const { data } = response;
                data.share_link_token && (setPublicLinkTokenFromDb(data.share_link_token))
                data?.is_share_link_enabled === 1 && (setIsPublicLinkOn(true))

                return data
            })
            .catch(({ message }) => {
                // toast.error(message, { position: toast.POSITION.TOP_RIGHT });
            });
    }

    // Load KML files, fences, and existing share-link config once on mount
    useEffect(() => {
        checkIfPublicLinkEnabled();

        setLayersLoading(true);
        let unsubKml: (() => void) | null = null;

        unsubKml = watchKmlFiles((files) => setKmlFiles(files));

        Promise.all([
            getAllFencesFlat(),
            getShareLinkConfig(tagId),
        ]).then(([fencesData, config]) => {
            setFences(fencesData);
            setShareLinkConfig(config);
        }).finally(() => {
            setLayersLoading(false);
        });

        return () => {
            if (typeof unsubKml === 'function') unsubKml();
        };
    }, [])

    const clientId = localStorage.get('active-client');
    const uid = useRedux((s) => s.auth.user.uid);

    const generatedLink = `${process.env.REACT_APP_FRONTEND_DOMAIN_URL}/#/public-view/?tag=${tagId}&client=${clientId}&token=${publicLinkTokenFromDb || ''}`;

    const setPublicLinkToDb = async (isEnabled) => {
        const nextToken = isEnabled ? (publicLinkTokenFromDb || nanoid()) : null;

        setIsPublicLinkOn(isEnabled)
        if (isEnabled) {
            setPublicLinkTokenFromDb(nextToken);
        } else {
            setPublicLinkTokenFromDb(null);
        }

        const URL = `/api/tag/store`;

        const body = {
            firebase_id: tagId,
            is_share_link_enabled: isEnabled ? 1 : 0,
            client_id: clientId,
            firebase_user_id: uid,
            share_link_token: nextToken,
        }

        await instance.post(URL, body)
            .then(response => {
                const { data } = response;

                return data
            })
            .catch(({ message }) => {
                setIsPublicLinkOn(!isEnabled);
                setPublicLinkTokenFromDb(isEnabled ? null : nextToken);
                // toast.error(message, { position: toast.POSITION.TOP_RIGHT });
            });
    }

    const handleKmlToggle = async (kmlId: string, checked: boolean) => {
        const file = kmlFiles[kmlId] || null;
        setShareLinkConfig((prev) => {
            const kml = { ...(prev.kml || {}) };
            if (checked && file) kml[kmlId] = file;
            else delete kml[kmlId];
            return { ...prev, kml };
        });
        await setShareLinkKml(tagId, kmlId, checked ? file : null);
    };

    const handleGeofenceToggle = async (fenceId: string, checked: boolean) => {
        const fence = fences[fenceId];
        const fenceEntry: iShareLinkFence | null = checked && fence ? {
            id: fence.id,
            name: fence.name,
            color: fence.color || '#ff7c02',
            region: fence.region,
            icon: fence.icon || false,
        } : null;
        setShareLinkConfig((prev) => {
            const geofences = { ...(prev.geofences || {}) };
            if (fenceEntry) geofences[fenceId] = fenceEntry;
            else delete geofences[fenceId];
            return { ...prev, geofences };
        });
        await setShareLinkGeofence(tagId, fenceId, fenceEntry);
    };

    const theme = createTheme(adaptV4Theme({
        overrides: {
            MuiSwitch: {
                track: {
                    "$checked$checked + &": {
                        opacity: 1.0,
                        backgroundColor: "rgb(255, 124, 2)"
                    },
                    "MuiIconButton-label": {
                        backgroundColor: "rgb(255, 124, 2)"
                    }
                }
            }
        }
    }));

    const [showWarnBlock, setShowWarnBlock] = useState(true);
    const [showCopied, setShowCopied] = useState(false);

    const handleCopyClick = () => {
        navigator.clipboard.writeText(generatedLink)
          .then(() => {
              setShowCopied(true);
              setTimeout(() => setShowCopied(false), 1000);
          }).finally(() => {
            setTimeout(() => setShowCopied(false), 3000);
        });
    }

    const kmlList = Object.values(kmlFiles);
    const fenceList = Object.values(fences).filter((f) => f && f.id && f.name && f.region && (f.region as any).shape !== FenceType.PATH);

    const closeModal = () => setIsPublicLinkModalOpen(false);
    return (
        <Modal className={styles.modal} onBackdropClick={closeModal}>
            <div className={styles.modalContainer}>
                <h1 className={styles.title}>Public Link</h1>
                {showWarnBlock && (
                  <WarningBlock
                    onClose={() => setShowWarnBlock(false)}
                    message='Enabling this switch will allow anyone with this link to view the location of devices from this tag'
                  />
                )}
                <label className={styles.switcher}>
                    <StyledEngineProvider injectFirst>
                        <ThemeProvider theme={theme}>
                            Enable public link <Switch checked={isPublicLinkOn} onChange={event => setPublicLinkToDb(event.target.checked)} />
                        </ThemeProvider>
                    </StyledEngineProvider>
                </label>
                {isPublicLinkOn && (
                    <>
                        <div className={styles.copyLinkSection}>
                            <a href={generatedLink} target='_blank' rel='noopener noreferrer' className={styles.publicLink}>{generatedLink}</a>
                            <button
                              type='button'
                              className={styles.copyButton}
                              onClick={handleCopyClick}
                            >
                                <CopyIcon className={styles.copyIcon} />
                                {showCopied ? 'Copied' : 'Copy Link'}
                            </button>
                        </div>

                        {/* Layer selection section */}
                        {layersLoading ? (
                            <div className={styles.layersLoading}>Loading layers…</div>
                        ) : (
                            <div className={styles.layersSection}>
                                {/* KML selection */}
                                {kmlList.length > 0 && (
                                    <div className={styles.layerGroup}>
                                        <div className={styles.layerGroupTitle}>KML Layers</div>
                                        <div className={styles.layerList}>
                                            {kmlList.map((file) => (
                                                <label key={file.id} className={styles.layerItem}>
                                                    <input
                                                        type="checkbox"
                                                        className={styles.layerCheckbox}
                                                        checked={!!(shareLinkConfig.kml?.[file.id])}
                                                        onChange={(e) => handleKmlToggle(file.id, e.target.checked)}
                                                    />
                                                    <span className={styles.layerItemName}>{file.name}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Geofence selection */}
                                {fenceList.length > 0 && (
                                    <div className={styles.layerGroup}>
                                        <div className={styles.layerGroupTitle}>Geofences</div>
                                        <div className={styles.layerList}>
                                            {fenceList.map((fence) => (
                                                <label key={fence.id} className={styles.layerItem}>
                                                    <input
                                                        type="checkbox"
                                                        className={styles.layerCheckbox}
                                                        checked={!!(shareLinkConfig.geofences?.[fence.id])}
                                                        onChange={(e) => handleGeofenceToggle(fence.id, e.target.checked)}
                                                    />
                                                    <span className={styles.layerItemName}>{fence.name}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {kmlList.length === 0 && fenceList.length === 0 && (
                                    <div className={styles.layersEmpty}>No KML layers or geofences available.</div>
                                )}
                            </div>
                        )}
                    </>
                )}

                <footer className={styles.footer}>
                    <button
                      type='button'
                      className={styles.closeButton}
                      onClick={closeModal}
                    >
                      close
                    </button>
                </footer>
            </div>
        </Modal>
    );
}

export default PublicLinkModal;
